import { Injectable } from '@angular/core';
import { getProjectResourcePath, TOKEN_CATEGORIES, TOKEN_TYPES, TOKEN_MODES, DESIGN_TOKENS_DIR_NAME, OVERRIDE_TOKENS_PATH, CACHE_TO_SUBTYPE_MAP } from '@wms/app/core';
import { runtimeEventManager } from "@wm/core";
import { BaseComponent, IWMSStandaloneEditorConstructionOptions } from '@wms/components';
import { FileService, PreviewService, ThemeService } from '@wms/app/services';
import { getTokens, Tokens, contrast, brighten, darken, getComponentsList} from '@wavemaker/foundation-css';
import { BehaviorSubject, Observable, Subject } from 'rxjs';

/**
 * Represents the structure of a token stored in the cache.
 */
export interface GlobalTokenData {
  name: string;
  value: string;
  opacity?: string | number;
  multiple?: string | number;
  reference?: string;
  derivedFrom?: string;
  formula?: string;
  subtype?: string;
}
type TokenCache = Record<string, any>;

export interface DropdownOption {
  colorHex?: string;
  value: string;
  text?: string;
  mode?: string;
  subtype? : string; 
}

@Injectable({
  providedIn: 'root'
})

export class FoundationService extends BaseComponent {
  baseTokensCache: TokenCache = {};
  compTokensCache: TokenCache = {};
  updatedTokensCache: Record<string, any> = {};
  updatedTokenSelectors: Record<string, any> = {};
  private updatedTokensCacheSubject = new BehaviorSubject<Record<string, any>>({});
  reloadAppOverridesSubject = new BehaviorSubject<boolean>(false);
  updatedTokensCache$: Observable<Record<string, any>> = this.updatedTokensCacheSubject.asObservable();
  reloadAppOverrides$: Observable<boolean> = this.reloadAppOverridesSubject.asObservable();
  private tokensClearedSubject = new Subject<void>();
  tokensCleared$ = this.tokensClearedSubject.asObservable();
  private resourceMngr: any;
  projectResourcesTree: any = [];
  overridePath = getProjectResourcePath(this.projectService.getProjectId()) + OVERRIDE_TOKENS_PATH;
  tokens = getTokens(this.projectService.getProjectPlatformType().toLowerCase() as any)
  dropdownCache: Record<string, DropdownOption[]> = {};
  tokenRegex: RegExp = /^(?:(?!\d+\.\d+$)[a-zA-Z0-9@_-]+\.)+[a-zA-Z0-9@_-]+$/
  styleConfigLabels: Record<string, string> = {
    foundation: 'LABEL_STYLE_GLOBAL_TOKENS',
    widgets: 'LABEL_STYLE_WIDGETS',
  };
  components = this.getComponentsList();
  projectOverridePath = `src/main/webapp/${OVERRIDE_TOKENS_PATH}`;

  jsonTextEditor: IWMSStandaloneEditorConstructionOptions = {
    value: '{"value": ""}',
    language: "json"
  };

  styleGlobalConfigTabs: Record<string, any> = {
    [TOKEN_TYPES.COLOR]: {
      label: 'LABEL_PROPERTY_COLOR',
      dependencies: [TOKEN_CATEGORIES.COLOR],
    },
    [TOKEN_TYPES.SIZE]: {
      label: 'LABEL_PROPERTY_BUBBLESIZE',
      dependencies: [TOKEN_CATEGORIES.SPACE, TOKEN_CATEGORIES.ICONS, TOKEN_CATEGORIES.BORDER]
    },
    [TOKEN_TYPES.TEXT]: {
      label: 'LABEL_WIDGET_TEXT',
      dependencies: [TOKEN_CATEGORIES.FONT]
    },
    [TOKEN_TYPES.STYLE]: {
      label: 'LABEL_PROPERTY_BORDERSTYLE',
      dependencies: [TOKEN_CATEGORIES.RADIUS, TOKEN_CATEGORIES.ELEVATION, TOKEN_CATEGORIES.OPACITY,TOKEN_CATEGORIES.BORDERSTYLE]
    },
  };

  categoryConfig: any = {
    [TOKEN_CATEGORIES.COLOR]: {
      modes: [TOKEN_MODES.DARK, TOKEN_MODES.LIGHT],
      dependencyTokens: [TOKEN_CATEGORIES.OPACITY],
      label: "LABEL_COLOR"
    },
    [TOKEN_CATEGORIES.RADIUS]: {
      label: "LABEL_PROPERTY_BORDERRADIUS"
    },
    [TOKEN_CATEGORIES.ICONS]: {
      label: "LABEL_ICONS"
    },
    [TOKEN_CATEGORIES.ELEVATION]: {
      label: "LABEL_ELEVATION"
    },
     [TOKEN_CATEGORIES.BORDER]: {
      label: "LABEL_PROPERTY_BORDER"
    },
     [TOKEN_CATEGORIES.BORDERSTYLE]: {
      label: "LABEL_PROPERTYGROUP_BORDER"
    },

    [TOKEN_CATEGORIES.OPACITY]: {},
    [TOKEN_CATEGORIES.SPACE]: {
      label: "LABEL_SPACING"
    },
    [TOKEN_CATEGORIES.FONT]: {
      modes: [],
      dependencyTokens: [],
      label: "LABEL_FONT"
    }
  };

  modesMap: Record<string, { label: string; icon: string }> = {
    [TOKEN_MODES.LIGHT]: {
      label: this.locale.LABEL_LIGHT,
      icon: 'wi wi-sun-o'
    },
    [TOKEN_MODES.DARK]: {
      label: this.locale.LABEL_DARK,
      icon: 'fa fa-moon-o'
    }
  };
  route: any;


  constructor(
    private fileService: FileService,
    private previewService: PreviewService,
    private themeService: ThemeService
  ) {
    super();
    this.resourceMngr = runtimeEventManager.getService('AppResourceManagerService');
    this.registerDestroyListener.add(this.eventManager.reloadDesignTokensAsObservable().subscribe(() => {
      this.reloadDesignTokens();
    }))
  }

  /*function to reload tokens*/
  reloadDesignTokens() {
    this.baseTokensCache = {};
    this.compTokensCache = {};
    this.dropdownCache = {};
    this.tokensClearedSubject.next();
    this.reloadAppOverridesSubject.next(true); 
  }

  /**
 * Loads token data based on category and mode, merges various token sources, and adds the transformed tokens to the cache.
 *
 * @param category - The category of tokens (e.g., 'color', 'font').
 * @param mode - (Optional) The mode of the tokens (e.g., 'light', 'dark').
 */
  async loadAndCacheTokens(category: string, mode?: string) {
    const cacheKey = category;
    this.initializeCache(cacheKey);
    this.dropdownCache[`${category}_${mode}`] = [];
    const [baseTokens, baseModeOverrides, overrides] = await this.fetchAllTokenData(category, mode);
    let mergedTokens = this.deepMerge(baseTokens, baseModeOverrides);
    mergedTokens = this.deepMerge(mergedTokens, overrides);
    const transformedTokens = this.transformData(mergedTokens, category, mode);
    this.mergeTransformedTokens(cacheKey, transformedTokens, mode);
  }

  getComponentsList() {
      const compDefs: any = getComponentsList(this.projectService.getProjectPlatformType().toLowerCase() as any);
      return compDefs;
    }

  private async fetchAllTokenData(category: string, mode?: string) {
    return await Promise.all([
      this.fetchBaseTokenData(category).catch(() => ({})),
      this.getBaseModeOverrideTokens(category, mode).catch(() => ({})),
      this.getOverrideTokens(category, mode).catch(() => ({})),
    ]);
  }

  /**
   * Fetches and filters token data by category and mode, resolving values, opacity, and multiplication factors.
   *
   * @param category - Token category (e.g., 'color', 'font').
   * @param mode - (Optional) Mode (e.g., 'light').
   * @returns An array of resolved tokens with values, opacity (default '100%'), and multiplication factor (default 1).
   */
  fetchAndTransformTokenData(
    category: string,
    mode?: string
  ): Record<string, any> {
    try {
      const cache = this.baseTokensCache[category];
      if (!cache) return {};

      const tokenHierarchy: Record<string, any> = {};
      const referencedParents = new Set();
      Object.values(cache).forEach((tokens: any) => {
        tokens.forEach((t: any) => {
          if (t.derivedFrom) {
            referencedParents.add(t.derivedFrom);
          }
        });
      });
      Object.entries(cache).forEach(([key, tokens]) => {
        (tokens as any[]).forEach((token) => {
          if (token['excludeFromUI'] == true) {
            return;
          }
          if (this.isTokenModeMatch(token, mode)) {
            const resolvedToken = this.processTokenDetails(key, token, category, mode);

            // Handle the font category specifically by grouping tokens under a parent token
            if (category === 'font') {
              this.groupFontTokens(tokenHierarchy, resolvedToken);
            }
            else {
              // If the token has a parent (derivedFrom), create or use the parent token
              if (resolvedToken.derivedFrom) {
                const parentName = resolvedToken.derivedFrom;
                let parentToken = tokenHierarchy[parentName];
                if (!parentToken) {
                  parentToken = this.createParentToken(parentName);
                  tokenHierarchy[parentName] = parentToken; // Ensure parent is in the hierarchy
                }
                parentToken.children = parentToken.children || [];
                parentToken.children.push(resolvedToken);
              } else {
                if (!referencedParents.has(resolvedToken.name) && category == "color") {
                  // custom and individual tokens → push into "others" for colors
                  const parentKey = TOKEN_TYPES.OTHERS;
                  const parentToken = tokenHierarchy[parentKey] ??= this.createParentToken(parentKey);
                  parentToken.displayName ||= parentKey;
                  parentToken.children ??= [];
                  parentToken.children.push(resolvedToken);
                } else {
                  // Otherwise, add the independent token to the hierarchy
                  tokenHierarchy[resolvedToken.name] = {
                    ...tokenHierarchy[resolvedToken.name],
                    ...resolvedToken
                  };
                }
              }
            }
          }
        });
      });

      return tokenHierarchy;
    } catch (error) {
      return {};
    }
  }


  private groupFontTokens(
    hierarchy: Record<string, any>,
    token: Record<string, any>
  ): void {
    const parts = token['name'].split('.');

    if (parts.length > 1) {
      const groupName = parts.slice(0, -1).join('.');  // e.g., body.large, h1
      const propertyName = parts.slice(-1).join('.');

      // Initialize group if not present
      if (!hierarchy[groupName]) {
        hierarchy[groupName] = { name: groupName, children: [] };  // Use an array for children
      }
      // Store each property under the group as an array element
      hierarchy[groupName].children.push({
        name: token['name'],
        value: token['value'],
        reference: token['reference'] || '',
        opacity: token['opacity'] || '100%',
        displayName: propertyName
      });
    } else {
      hierarchy[token['name']] = token;
    }
  }


  // Resolves token data and returns the transformed token object
  private processTokenDetails(key: string, token: any, category: string, mode?: string): any { 
    return {
      name: key,
      value: this.updatedTokensCache?.[category]?.[key] || (token.multiple ? this.calculateMultipliedValue(this.resolveTokenValue(token.value, mode), token.multiple) : this.resolveTokenValue(token.value, mode)),
      reference: this.transformKey(this.updatedTokensCache?.[category]?.[key] || token.value),
      opacity: this.resolveTokenValue(token.opacity, mode) || "100%",
      ...(token.multiple && { multiple: this.resolveTokenValue(token.multiple, mode) ?? 1, }),
      derivedFrom: token.derivedFrom,
      displayName: this.transformKey(key),
      formula: token.formula, 
      subtype: token.subtype || token.type, 
    };
  }

  // Creates a parent token if it doesn't exist
  private createParentToken(parentName: string) {
    return {
      name: parentName,
      value: "",
      reference: "",
      opacity: "100%",
      multiple: 1,
      children: [],
    };
  }


  /**
 * Resolves token references in a string by replacing them with their actual values.
 *
 * @param value - The input string with token references (e.g., `{font.weight.bold}`).
 * @param mode - (Optional) Mode to use for resolving tokens (e.g., "light", "dark").
 * @returns The string with all token references resolved.
 */
  resolveTokenValue(value: string, mode?: string): string {
    if (typeof value !== 'string') return value; // Return non-string values as is

    const referenceRegex = /\{([^}]+)\}/g;

    const resolveReferences = (input: string): string =>
      input.replace(referenceRegex, (_, referencePath) =>
        this.getTokenValue(referencePath, mode) || referencePath
      );

    let resolvedValue = value;
    let hasUnresolvedReferences = true;

    // Resolve references until all are replaced
    while (hasUnresolvedReferences) {
      const nextValue = resolveReferences(resolvedValue);
      hasUnresolvedReferences = nextValue !== resolvedValue;
      resolvedValue = nextValue;
    }

    return resolvedValue;
  }


  /**
  * Retrieves the value of a token from baseTokensCache based on its path, mode, and token type.
  *
  * @param path - The dot-separated token path (e.g., "color.background.primary").
  * @param activeMode - (Optional) The active mode (e.g., "light", "dark").
  * @returns The token value if found; otherwise, `undefined`.
  */
  getTokenValue(path: string, activeMode?: string): string | undefined {
    const pathSegments = path.split('.');

    const category = pathSegments[0];
    const baseKey = pathSegments.slice(0, -1).join('.');
    const tokens = this.baseTokensCache[category]?.[baseKey];
    const override = this.updatedTokensCache[category]?.[baseKey];

    if (override) return override; // Return override if it exists
    if (!tokens || !Array.isArray(tokens)) return undefined;

    return this.findMatchingToken(tokens, activeMode)?.value;
  }

  async fetchBaseTokenData(category: string): Promise<any> {
    const tokenSource = this.tokens as Tokens;
    const selectedTokens = tokenSource?.[category];
    if (selectedTokens?.default) {
      return Promise.resolve(selectedTokens.default);
    }

    return Promise.resolve({});
  }

  async getOverrideTokens(category: string, mode?: string, fileCategory?: string ): Promise<any> {
    const fileName = mode ? `${category}.${mode}.json` : `${category}.json`;
    const categoryType = fileCategory ? fileCategory :
      this.isGlobalCategory(category) ? this.locale.LABEL_STYLE_GLOBAL_TOKENS.toLowerCase() : this.locale.LABEL_STYLE_WIDGETS.toLowerCase();
    const targetPath = `${this.projectOverridePath}/${categoryType}/${category}/${fileName}`;
    const overrideUrl = `${this.overridePath}/${categoryType}/${category}/${fileName}`;

    if (!this.hasFileInProject(this.projectResourcesTree, targetPath)) {
      return {};
    }

    try {
      return await this.loadFile(overrideUrl);
    } catch {
      return {};
    }
  }

  async getBaseModeOverrideTokens(category: string, mode: string = "default"): Promise<any> {
    const tokenSource = this.tokens as unknown as Tokens;
    const selectedTokens = tokenSource?.[category];

    if (selectedTokens && mode in selectedTokens) {
      const tokens = (selectedTokens as Record<string, any>)[mode] ?? {};
      return Promise.resolve(tokens);
    }
    return Promise.resolve({});
  }


  transformData(data: Record<string, any>, category: string, mode?: string): Record<string, any> {
    if (!data || typeof data !== 'object') return {};
    const result: Record<string, any> = {};
    const processObject = (obj: Record<string, any>, parentKey: string): void => {
      Object.keys(obj).forEach(key => {
        const fullKey = parentKey ? `${parentKey}.${key}` : key;
        const value = obj[key];
        if (value?.value) {
          result[fullKey] = {
            value: value.value,
            opacity: value.attributes?.opacity ?? "100%",
            category,
            ...(value.attributes?.multiple && { multiple: value.attributes?.multiple ?? 1 }),
            ...(mode && { mode }),
            ...(value.attributes?.derivedFrom && { derivedFrom: value.attributes?.derivedFrom }),
            ...(value.attributes?.formula && { formula: value.attributes?.formula }),
            ...(value.attributes?.excludeFromUI && { excludeFromUI: value.attributes?.excludeFromUI }), 
            ...( (value.attributes?.subtype || value.type) && { subtype: value.attributes?.subtype ?? value.type }),
          };
        } else if (typeof value === 'object') {
          processObject(value, fullKey);
        }
      });
    };

    processObject(data, ''); 
    return result;
  }


  // Saves the updated tokens to the overrides folder, merging them with existing tokens for the specified category and mode.
  async saveOverides(mode?: string) {
    const categories = Object.keys(this.updatedTokensCache || {});
    const modeSupportedCategories = [TOKEN_TYPES.COLOR];  // Add other categories as needed
    // Categories that exist in both global + widgets scope
    const dualScopeCategories = [TOKEN_CATEGORIES.ICONS];
    const LABEL_GLOBAL = this.locale.LABEL_STYLE_GLOBAL_TOKENS.toLowerCase();
    const LABEL_WIDGET = this.locale.LABEL_STYLE_WIDGETS.toLowerCase();
    for (const cat of categories) {
      const useMode = modeSupportedCategories.includes(cat);
      const categoryType = this.isGlobalCategory(cat) ? LABEL_GLOBAL : LABEL_WIDGET;
      const config = { cat, useMode, mode };
      // Handle categories that exist in both global + widgets (e.g., icon)
      if (dualScopeCategories.includes(cat) && this.isGlobalCategory(cat)) {
        const tokens = this.updatedTokensCache[cat] || {};
        const globalTokens: Record<string, any> = {};
        const widgetTokens: Record<string, any> = {};

        for (const key of Object.keys(tokens)) {
          if (key.includes('.appearances.') || key.includes('.variantGroups.') || key.includes('.mapping.')) {
            widgetTokens[key] = tokens[key];
          } else {
            globalTokens[key] = tokens[key];
          }
        }
        await this.saveTokensToPath({ ...config, tokens: globalTokens, categoryType: LABEL_GLOBAL });
        await this.saveTokensToPath({ ...config, tokens: widgetTokens, categoryType: LABEL_WIDGET });
        continue;
      }
      await this.saveTokensToPath({ ...config, tokens: this.updatedTokensCache[cat], categoryType });
    }
    this.clearUpdatedTokensCache();
  }



  /**
   * Utility function for writing a category’s tokens to its override file.
   */
  private async saveTokensToPath(config: any) {
    const { cat, tokens, categoryType, useMode, mode } = config;
    if (!tokens || Object.keys(tokens).length === 0) return;
      const basePath = `${OVERRIDE_TOKENS_PATH}/${categoryType}/${cat}`;
      const overrideUrl = `${basePath}/${cat}${useMode && mode ? `.${mode}` : ""}.json`;

      const overrideTokens = await this.getOverrideTokens(cat, useMode ? mode : undefined, categoryType) || {};
      const nestedTokens = this.convertFlatToNestedStructure(tokens);
      const mergedTokens = this.deepMerge(overrideTokens, nestedTokens);

      await this.writeToFile(overrideUrl, JSON.stringify(mergedTokens, null, 2), cat).then(() => this.reloadAppOverridesSubject.next(true))
  }

  /**
    Converts a flat object with dot notation keys into a nested object,
     with the final key's value assigned as a 'value' property.
    For example, { 'color.primary.@.value': 'red' } becomes
    { color: { primary: { '@': { value: 'red' } } } }.
  */
  convertFlatToNestedStructure(flatObject: Record<string, any>): Record<string, any> {
    const nestedObject: Record<string, any> = {};

    for (const key in flatObject) {
      const value = flatObject[key];
      const keyParts = key.split('.');

      keyParts.reduce((acc, part, index) => {
        if (index === keyParts.length - 1) {
          acc[part] = { value };
        } else {
          acc[part] = acc[part] || {};
        }
        return acc[part];
      }, nestedObject);
    }

    return nestedObject;
  }

  evaluateFormula(formula: string, tokenValue: string): string {
    if (formula.includes('lighten')) {
      return brighten(tokenValue, formula);
    } else if (formula.includes('darken')) {
      return darken(tokenValue, formula);
    } else if (formula.includes('contrast')) {
      return contrast(tokenValue);
    }
    return tokenValue; // Return original value if no formula is matched
  }

  /**
  * Loads the project resource data through API call to file service
  * transform the data into the data that the tree component accepts
  * feed the transformed data to this.projectResourceTree
  */
  async loadProjectResourcesTree(): Promise<void> {
    try {
      const data = await this.fileService.readDir(`../project/src/main/webapp/${DESIGN_TOKENS_DIR_NAME}/`);
      this.projectResourcesTree = data;
    } catch (error) {
      this.projectResourcesTree = [];
    }
  }

  hasFileInProject(data: any, targetPath: string): boolean {
    // Check if the current item has the desired path
    if (data.path === targetPath) {
      return true;
    }

    // Check in files array if it exists
    if (data.files && Array.isArray(data.files)) {
      for (const file of data.files) {
        if (this.hasFileInProject(file, targetPath)) {
          return true;
        }
      }
    }
    return false;
  }


  /**
 * Merges two jsons, recursively updating the target with properties from the source.
 *
 * @param target - Existing data.
 * @param source - New or updated data.
 *
 * Example:
 * Input: target = { "color": { "primary": { "value": "red" } } },
 *  source = { "color": { "primary": { "value": "green" }, "tertiary": { "value": "blue" } } }
 * Output: { "color": { "primary": { "value": "green" }, "tertiary": { "value": "blue" } } }
 */
  private deepMerge(target: any, source: any): any {
    const clonedTarget = { ...target };  // Clone to avoid mutation
    if (typeof source !== 'object' || !source) return clonedTarget;

    Object.keys(source).forEach(key => {
      const sourceValue = source[key];
      const targetValue = clonedTarget[key];

      if (sourceValue && typeof sourceValue === 'object' && !Array.isArray(sourceValue)) {
        clonedTarget[key] = this.deepMerge(targetValue || {}, sourceValue);
          // Handle "multiple" attribute from overrides
        if (targetValue?.attributes && targetValue?.attributes?.multiple) {
          clonedTarget[key].attributes = clonedTarget[key]?.attributes || {};
          clonedTarget[key].attributes.multiple =
            sourceValue?.attributes?.multiple ?? "1";  // Else, set default to "1"
          if (targetValue?.attributes?.derivedFrom) {
            clonedTarget[key].attributes.derivedFrom =
              sourceValue?.attributes?.derivedFrom ?? null;
          }
        }
      } else {
        clonedTarget[key] = sourceValue;
      }
    });

    return clonedTarget;
  }


  private mergeTransformedTokens(cacheKey: string, transformedTokens: any, mode?: string) {
    for (const key in transformedTokens) {
      if (transformedTokens.hasOwnProperty(key)) {
        this.addTokenToCache(cacheKey, key, transformedTokens[key]);
      }
    }
  }


  /**
  * Adds tokens to the cache, storing them by category (e.g., 'color', 'font') and token key (e.g., 'primary.@', 'h1').
  *
  * Example:
  * baseTokensCache = {
  *   color: {
  *     "color.primary.@": [
  *       { value: "purple", opacity: "100%", multiple: 1, category: "color", mode: "light" },
  *       { value: "#000", opacity: "100%", multiple: 1, category: "color", mode: "dark" }
  *     ]
  *   },
  *   font: { "font.h1": [] }
  * }
  */
  private addTokenToCache(cacheKey: string, key: string, newToken: any) {
    if (!this.baseTokensCache[cacheKey][key]) {
      this.baseTokensCache[cacheKey][key] = [];
    }  

    const isDuplicate = this.baseTokensCache[cacheKey][key].some(
      (cachedToken: any) =>
        cachedToken.mode === newToken.mode &&
        JSON.stringify(cachedToken) === JSON.stringify(newToken)
    );

    // Add the token only if it's not a duplicate for the same mode
    if (!isDuplicate) {
      this.baseTokensCache[cacheKey][key] = this.baseTokensCache[cacheKey][key].filter(
        (cachedToken: any) => cachedToken.mode !== newToken.mode
      );
      this.baseTokensCache[cacheKey][key].push(newToken);
    }
    this.addToDropdownCache(cacheKey, key, newToken);
  }


  objectKeys(obj: any): string[] {
    return obj ? Object.keys(obj) : [];
  }

  getCategoryDependencies(category: string): string[] {
    const tab = this.styleGlobalConfigTabs[category];
    return tab ? tab.dependencies : []; // Return the load array or an empty array if not found
  }

  calculateMultipliedValue(value: string, multiplier: string | number): string {
    if (!value || !multiplier) {
      return value;
    }
    const numericValue = parseFloat(value);
    const multiplierValue = parseFloat(multiplier.toString());

    if (isNaN(numericValue) || isNaN(multiplierValue)) {
      return value;
    }

    const unit = value.replace(/[\d.]/g, '').trim();
    const result = (numericValue * multiplierValue).toFixed(2); // Round to 2 decimal places
    return `${parseFloat(result)}${unit}`;  // Remove trailing zeros
  }


  async updateTokenToCache(category: string, data: Record<string, any>, newPrimaryValue: string,meta?: { selector?: string; name?: string }) {
    // Ensure the category exists in the updatedTokensCache, or initialize it as an empty object
    this.updatedTokensCache[category] = this.updatedTokensCache[category] || {};
    this.updatedTokenSelectors[category] ??= {};
    const updateTokensCache = {};

    // Update the value of the current data object
    data['value'] = newPrimaryValue;
    this.updatedTokensCache[category][data['name']] = newPrimaryValue;

    // Update selector mapping if metadata provided
    if (meta?.selector || meta?.name) {
      this.updatedTokenSelectors[category][data?.['name']] = {
        ...(meta.selector && { selector: meta.selector }),
        ...(meta.name && { name: meta.name })
      };
    }

    // Iterate through children and update their values if necessary
    data['children']?.forEach((child: any) => {
      if (child.multiple) {
        const updatedValue = this.calculateMultipliedValue(newPrimaryValue, child.multiple);
        child.value = updatedValue;
      }
      if (child.formula) {
        const updatedValue = this.evaluateFormula(child.formula, newPrimaryValue);
        child.value = updatedValue;
        this.updatedTokensCache[category][child.name] = updatedValue;
      }
    });
    this.updatedTokensCacheSubject.next(data);

    // Return the updated tokens cache for this category
    return updateTokensCache;
  }

  // Helper function: Checks if token's mode matches the requested mode
  private isTokenModeMatch(token: any, mode?: string): boolean {
    return mode ? token.mode === mode : true;
  }

  // Finds the token that matches the active mode and token type,
  // falling back to mode, token type, or the first token if no match is found.
  private findMatchingToken(tokens: any[], activeMode?: string) {
    return (
      tokens.find(item => item.mode === activeMode) ||
      tokens[0]
    );
  }

  private async writeToFile(overrideUrl: string, content: string, category: string) {
    await this.fileService.write(overrideUrl, content)
  }

  isColorCategory(categoryKey: string): boolean {
    if (!categoryKey) {
      return false;
    }
    return categoryKey === TOKEN_CATEGORIES.COLOR;
  }

  isFontCategory(categoryKey: string): boolean {
    return categoryKey === TOKEN_CATEGORIES.FONT;
  }

  private initializeCache(cacheKey: string) {
    if (!this.baseTokensCache[cacheKey]) {
      this.baseTokensCache[cacheKey] = {};
    }
  }

 loadFile(url: string) {
    return this.resourceMngr.get(url, false);
  }

  clearUpdatedTokensCache() {
    let shouldClearCompCache = false;
    Object.keys(this.updatedTokensCache).forEach((key) => {
      if (this.baseTokensCache[key]) {
        delete this.baseTokensCache[key];
        shouldClearCompCache = true; // Mark compTokensCache for updates
      }
      if (this.compTokensCache[key]) {
        delete this.compTokensCache[key]; // Remove only updated components from components cache
      }
    });

    if (shouldClearCompCache) {
      this.compTokensCache = {}; // Clear entire components cache if any global token was edited
    }
    this.updatedTokensCache = {};
    this.updatedTokensCacheSubject.next({});
  }

  /**
   * Transforms a key by removing unwanted parts: .@, .value, {, and }
   * @param {string} key - The input key to transform
   * @returns {string} - The transformed key
   */
  transformKey(key: string) {
    return typeof key !== "string" ? key : key.replace(/^[^.]+\./, "").replace(/(\.@|\.value|[{}])/g, "");
  }


  getTokensCount() {
    let totalCount = 0;
    // Iterate over all categories in updatedTokensCache
    for (const category in this.updatedTokensCache) {
      if (this.updatedTokensCache.hasOwnProperty(category)) {
        const tokenData = this.updatedTokensCache[category];

        // If tokenData exists, add the number of keys to the total count
        if (tokenData) {
          totalCount += Object.keys(tokenData).length;
        }
      }
    }
    return totalCount;  // Return the total count of properties for all categories
  }


  /**
   * Merge the updated tokens into a single object, converting each token into its nested form.
   * @param keys - The keys representing the updated tokens.
   * @returns A merged object containing all updated tokens.
   */
  mergeUpdatedTokens(keys: string[]): Record<string, any> {
    return keys.reduce((acc: Record<string, any>, key: string) => {
      const updatedToken = this.updatedTokensCache[key];
      if (updatedToken) {
        const convertedObject = this.convertFlatToNestedStructure(updatedToken);
        Object.assign(acc, convertedObject);
      }
      return acc;
    }, {});
  }

    /**
 * Retrieve the keys of updated tokens from the cache.
 * @returns An array of keys representing updated tokens.
 */
    getUpdatedTokenKeys(): string[] {
      return Object.keys(this.updatedTokensCache || {});
    }

    updateJsonTextEditor(mergedTokens: Record<string, any>) {
      this.jsonTextEditor.value = JSON.stringify(mergedTokens, null, 2);
    }

   async loadTokensForSelectedCategory(category: string, modes: string[]) {
    if (modes?.length > 0) {
      for (const mode of modes) {
        await this.loadAndCacheTokens(category, mode);
      }
    } else {
      await this.loadAndCacheTokens(category);
    }
  }

  private addToDropdownCache(cacheKey: string, key: string, newToken: any) {
    const dropdownKey = newToken.mode ? `${cacheKey}_${newToken.mode}` : cacheKey;
    const isColorToken = this.isColorCategory(newToken.category);
    if (!this.dropdownCache[dropdownKey]) {
      this.dropdownCache[dropdownKey] = [];
    }
    
    const isDropdownDuplicate = this.dropdownCache[dropdownKey].some(
      option => option.value === key && option.mode === newToken.mode
    );

    if (!isDropdownDuplicate) {
      this.dropdownCache[dropdownKey].push({
        value: key,
        mode: newToken.mode,
        subtype : newToken.subtype || this.subtypeCategorization(key, cacheKey), 
        ... (!isColorToken && { text: this.resolveTokenValue(newToken.value) }),
        ... (isColorToken && { colorHex: this.resolveTokenValue(newToken.value) }), 
      });
    }
  }

  private isGlobalCategory(cat: string): boolean {
    const baseCats = Object.keys(this.tokens);
    return baseCats.includes(cat);
  }

  async loadTokensForAllCategories(categories: string[], forceLoad: boolean = false) {
    await this.loadProjectResourcesTree();
    for (const category of categories) {
      if (!this.baseTokensCache[category] || forceLoad) {
        const { modes, dependencyCategories } = this.categoryConfig[category];
        await this.loadTokensForSelectedCategory(category, modes);
        if (dependencyCategories?.length > 0) {
          for (const depCategory of dependencyCategories) {
            const dependencyConfig = this.categoryConfig[depCategory];
            if (dependencyConfig) {
              const { modes: depModes } = dependencyConfig;
              await this.loadTokensForSelectedCategory(depCategory, depModes);
            }
          }
        }
      }
    }
  }

  isCategoryMatch(category: string, categoryToCheck: string): boolean {
    return category === categoryToCheck;
  }

  async saveVariantOverride(category: string, tokens: any) {
    const basePath = `${OVERRIDE_TOKENS_PATH}/components/${category}`;
    const overrideUrl = `${basePath}/${category}.json`;
    await this.writeToFile(overrideUrl, JSON.stringify(tokens, null, 2), category).then(() => this.reloadAppOverridesSubject.next(true));
  }

  private subtypeCategorization(key: string, cacheKey: string){
    if (cacheKey == "font") {
      const split = key.split("."); 
      let subtype = ""; 
      if (split.includes("font-family") || split.includes("family")){
         subtype = "font-family"
      }
      if (split.includes("font-size") || split.includes("size")){
        subtype = "font-size"
      }
      if (split.includes("font-weight") || split.includes("weight")){
      subtype = "font-weight"
      }
      if (split.includes("letter-spacing")){
        subtype = "letter-spacing"
      }
      if (split.includes("line-height")){
        subtype = "line-height"
      }

      return subtype
    }

    const finalKey = CACHE_TO_SUBTYPE_MAP[cacheKey] ? CACHE_TO_SUBTYPE_MAP[cacheKey] : cacheKey
    return finalKey

  }
}
